import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import TruncatedEllipsis from '../../shared/truncated-ellipsis';
import WireCaption from '../../wire/wire-caption';
import { recordStatisticsEvent } from '../../../services/statistics-service';
import { useGetPaginatedWiresMentioningContributor } from '../../../services/wires/from-contributor-service';
import {
  SectionContainer,
  SectionTitle,
  SectionSubTitle,
  SectionDetails,
} from '../../shared/sectionable/styles';
import Tail from '../common/styles';
import { scrollIfHashMatch } from '../../shared/navigation/utils';
import useContributorStateStore from '../common/contributor-state-hook';

const WiresContainer = styled.ul`
  display: flex;
  flex-direction: row;
  list-style: none;
  padding: 0;
  margin: 0 0 40px;

  &:last-of-type {
    margin: 0;
  }

  ${props => props.theme.isMobileOrTablet && `
    flex-direction: column;
  `};
`;

const Wire = styled.li`
  min-width: 33.33%;
  padding-right: 20px;

  &.fixed-width {
    width: 33.33%;
  }

  ${props => props.theme.isMobileOrTablet && `
    margin-bottom: 32px;
    padding: 0;

    &:last-child {
      margin-bottom: 0;
    }

    &.fixed-width {
      width: 100%;
    }
  `};
`;

const CaptionRow = styled.div`
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: 800;
  font-size: 12px;
  line-height: 120%;
  text-transform: uppercase;

  & > span.separator {
    font-size: 16px;
    font-weight: ${props => props.theme.fontWeightBolder};
  }
`;

const WireTitle = styled.a`
  display: block;
  color: ${props => props.theme.bodyColor};
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: 800;
  font-size: 16px;
  line-height: 120%;
  text-transform: uppercase;
  margin-top: 4px;

  &:hover {
    color: ${props => props.theme.bodyColor};
  }
`;

const Author = styled.a`
  display: block;
  color: ${props => props.theme.secondaryColor};
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: 800;
  text-transform: uppercase;
  font-size: 14px;
  line-height: 120%;
  margin-top: 8px;

  &:hover {
    color: ${props => props.theme.secondaryColor};
  }
`;

const Organisation = styled.div`
  color: ${props => props.theme.secondaryColor};
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightBold};
  font-size: 12px;
  line-height: 120%;
  margin-top: 4px;
`;

const INIT_WIRE_NUMBER = 3;
const MAX_WIRE_NUMBER = 9;

const sectionName = 'mentions';

export default function Mention({ profile }) {
  const { isXLarge, isMobileOrTablet } = React.useContext(ThemeContext);
  const {
    id, slug, name, organization, isCompanyProfile,
  } = profile;
  const [{ data }, getMentionWires] = useGetPaginatedWiresMentioningContributor(id);
  const [wires, setWires] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [showViewMore, setShowViewMore] = React.useState(false);
  const [showViewAll, setShowViewAll] = React.useState(false);
  const set = useContributorStateStore(state => state.set);

  React.useEffect(() => {
    getMentionWires(1);
  }, [getMentionWires]);

  React.useEffect(() => {
    const totalCount = _.get(data, 'total') || 0;
    setTotal(totalCount);
    setShowViewMore(totalCount > INIT_WIRE_NUMBER);
    setWires(_.get(data, 'wires') || []);
    set(state => {
      state.hasMentions = totalCount > 0;
    });
  }, [data, set]);

  useChangeAwareEffect(({ isMount }) => {
    isMount && scrollIfHashMatch(sectionName);
  }, {});

  if (wires.length === 0) {
    return null;
  }

  const buildWireTitle = title => {
    if (isMobileOrTablet) {
      return title;
    }

    const lines = isXLarge ? 4 : 5;

    return (
      <TruncatedEllipsis
        lines={lines}
        summary={title}
        useReadMore={false}
        hasTags={false}
      />
    );
  };

  const onViewMore = e => {
    e && e.preventDefault();
    recordStatisticsEvent({
      event: 'contributor_profile_mentioned_view_more_clicked',
      statisticable: 'profile',
      id,
      page: window.location.pathname,
    });
    setWires(_.slice(data.wires, 0, MAX_WIRE_NUMBER));
    setShowViewMore(false);
    setShowViewAll(total > MAX_WIRE_NUMBER);
  };

  const onViewAll = e => {
    e && e.preventDefault();
    recordStatisticsEvent({
      event: 'contributor_profile_mentioned_view_all_clicked',
      statisticable: 'profile',
      id,
      page: window.location.pathname,
    });
    window.location = `/search/contributor/${slug}?nextPage=true&tab=mentions`;
  };

  const wiresToShow = _.slice(wires, 0, showViewMore ? INIT_WIRE_NUMBER : MAX_WIRE_NUMBER);
  const wireClassName = wiresToShow.length > INIT_WIRE_NUMBER ? 'fixed-width' : '';

  const handleWireClick = wire => {
    recordStatisticsEvent({
      event: 'contributor_profile_mentioned_wire_view',
      statisticable: 'profile',
      id,
      clicked_url: `/wires/${wire.slug}`,
      page: window.location.pathname,
    });

    return false;
  };

  const showCompanyName = isCompanyProfile && !!organization;

  return (
    <SectionContainer name={sectionName}>
      <SectionTitle>
        Mentions
        <SectionSubTitle>{`Recent wires which mention ${showCompanyName ? organization : name}`}</SectionSubTitle>
      </SectionTitle>
      <SectionDetails>
        {_.chunk(wiresToShow, INIT_WIRE_NUMBER).map((group, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <WiresContainer key={`row-${index}`}>
            {group.map(wire => (
              <Wire key={`wire-${wire.id}`} className={wireClassName}>
                <CaptionRow>
                  <WireCaption wire={wire} />
                </CaptionRow>
                <WireTitle href={`/wires/${wire.slug}`} onClick={() => handleWireClick(wire)}>
                  {buildWireTitle(wire.title)}
                </WireTitle>
                <Author href={`/contributors/${wire.profile.slug}`}>
                  {wire.profile.isCompanyProfile && !!wire.profile.organization
                    ? wire.profile.organization : wire.profile.name}
                </Author>
                {!wire.profile.isCompanyProfile
                    && (
                    <Organisation>
                      {wire.profile.organization}
                    </Organisation>
                    )}
              </Wire>
            ))}
          </WiresContainer>
        ))}
        {showViewMore && (
          <Tail>
            <button type="button" onClick={onViewMore} className="view-more">view more</button>
          </Tail>
        )}
        {showViewAll && (
          <Tail>
            <button type="button" onClick={onViewAll} className="view-all">view all mentions</button>
          </Tail>
        )}
      </SectionDetails>
    </SectionContainer>
  );
}

Mention.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
    organization: PropTypes.string,
    isCompanyProfile: PropTypes.bool,
  }).isRequired,
};
